/**
 * Consent Management Component
 * GDPR-compliant consent management UI
 */

import React, { useEffect, useState } from "react";
import {
  consentService,
  ConsentType,
  ConsentRecord,
  ConsentStatus,
} from "../../services/consent.service";
// import { useI18nStore } from '../../stores/i18nStore'; // Uncomment when translations are added
import toast from "react-hot-toast";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { Shield, Check, X, History, Info } from "lucide-react";

interface ConsentToggle {
  type: ConsentType;
  labelKey: string;
  descriptionKey: string;
  category:
    | "data"
    | "sharing"
    | "communication"
    | "analytics"
    | "marketing"
    | "special";
  required: boolean;
}

const consentToggles: ConsentToggle[] = [
  // Data Processing (Required)
  {
    type: ConsentType.DATA_PROCESSING,
    labelKey: "Data Processing",
    descriptionKey: "Process your health data to provide the service",
    category: "data",
    required: true,
  },
  {
    type: ConsentType.DATA_STORAGE,
    labelKey: "Data Storage",
    descriptionKey: "Store your health data securely",
    category: "data",
    required: true,
  },

  // Sharing
  {
    type: ConsentType.SHARE_WITH_HEALTHCARE_PROVIDERS,
    labelKey: "Share with Healthcare Providers",
    descriptionKey: "Allow sharing your data with healthcare providers",
    category: "sharing",
    required: false,
  },
  {
    type: ConsentType.SHARE_WITH_TOUR_OPERATORS,
    labelKey: "Share with Tour Operators",
    descriptionKey: "Allow sharing travel health data with tour operators",
    category: "sharing",
    required: false,
  },
  {
    type: ConsentType.SHARE_WITH_INSURERS,
    labelKey: "Share with Insurers",
    descriptionKey: "Allow sharing relevant data with insurance companies",
    category: "sharing",
    required: false,
  },

  // Communication
  {
    type: ConsentType.EMAIL_COMMUNICATIONS,
    labelKey: "Email Notifications",
    descriptionKey: "Receive health reminders and updates via email",
    category: "communication",
    required: false,
  },
  {
    type: ConsentType.SMS_NOTIFICATIONS,
    labelKey: "SMS Notifications",
    descriptionKey: "Receive important health alerts via SMS",
    category: "communication",
    required: false,
  },
  {
    type: ConsentType.PUSH_NOTIFICATIONS,
    labelKey: "Push Notifications",
    descriptionKey: "Receive app notifications",
    category: "communication",
    required: false,
  },

  // Analytics
  {
    type: ConsentType.USAGE_ANALYTICS,
    labelKey: "Usage Analytics",
    descriptionKey: "Help improve the app by sharing anonymous usage data",
    category: "analytics",
    required: false,
  },
  {
    type: ConsentType.PERFORMANCE_MONITORING,
    labelKey: "Performance Monitoring",
    descriptionKey: "Allow monitoring app performance to improve stability",
    category: "analytics",
    required: false,
  },

  // Marketing
  {
    type: ConsentType.MARKETING_COMMUNICATIONS,
    labelKey: "Marketing Communications",
    descriptionKey: "Receive news and offers from HealthPass",
    category: "marketing",
    required: false,
  },

  // Special
  {
    type: ConsentType.INTERNATIONAL_UNIT_CONVERSION,
    labelKey: "International Unit Conversion",
    descriptionKey:
      "Allow automatic unit conversion for international providers",
    category: "special",
    required: false,
  },
  {
    type: ConsentType.FHIR_DATA_SHARING,
    labelKey: "Health Data Sharing",
    descriptionKey:
      "Share health data in a standardised format with medical systems",
    category: "special",
    required: false,
  },
];

export const ConsentManagement: React.FC = () => {
  // const { t } = useI18nStore(); // Uncomment when translations are added
  const fmt = useLocaleFormat();
  const [consents, setConsents] = useState<Map<ConsentType, boolean>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<ConsentRecord[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadConsents();
  }, []);

  const loadConsents = async () => {
    try {
      setLoading(true);
      const activeConsents = await consentService.getActiveConsents();
      const allConsents = await consentService.getMyConsents();

      const consentMap = new Map<ConsentType, boolean>();
      consentToggles.forEach((toggle) => {
        const hasConsent = activeConsents.some(
          (c) => c.consentType === toggle.type,
        );
        consentMap.set(toggle.type, hasConsent);
      });

      setConsents(consentMap);
      setHistory(allConsents);
    } catch (error: any) {
      console.error("Failed to load consents:", error);
      toast.error("Failed to load consent settings");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (
    consentType: ConsentType,
    toggle: ConsentToggle,
  ) => {
    const currentValue = consents.get(consentType) || false;
    const newValue = !currentValue;

    if (toggle.required && !newValue) {
      toast.error("This consent is required to use the service");
      return;
    }

    try {
      if (newValue) {
        await consentService.grantConsent(
          consentType,
          toggle.descriptionKey,
          "Consent",
          "1.0",
        );
        toast.success(`✅ ${toggle.labelKey} consent granted`, {
          duration: 2000,
        });
      } else {
        await consentService.withdrawConsent(
          consentType,
          "User withdrew consent",
        );
        toast.success(`❌ ${toggle.labelKey} consent withdrawn`, {
          duration: 2000,
        });
      }

      // Update local state
      const newConsents = new Map(consents);
      newConsents.set(consentType, newValue);
      setConsents(newConsents);

      // Reload history
      loadConsents();
    } catch (error: any) {
      console.error("Failed to update consent:", error);
      toast.error("Failed to update consent");
    }
  };

  const getCategoryLabel = (category: string): string => {
    const labels: Record<string, string> = {
      data: "📊 Data Processing",
      sharing: "🔗 Data Sharing",
      communication: "📧 Communications",
      analytics: "📈 Analytics",
      marketing: "📢 Marketing",
      special: "⚕️ Special Permissions",
    };
    return labels[category] || category;
  };

  const groupedToggles = consentToggles.reduce(
    (acc, toggle) => {
      const category = toggle.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category]!.push(toggle);
      return acc;
    },
    {} as Record<string, ConsentToggle[]>,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⚙️</div>
          <p className="text-gray-600">Loading consent settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
          <h2 className="text-3xl font-bold text-gray-900">
            Consent Management
          </h2>
        </div>
        <p className="text-gray-600">
          Manage your privacy preferences and data sharing consents. You can
          change these settings at any time.
        </p>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">GDPR Compliance</p>
          <p>
            Your data is protected under GDPR regulations. Required consents are
            necessary to provide the service. Optional consents enhance your
            experience but can be withdrawn at any time.
          </p>
        </div>
      </div>

      {/* Consent Toggles by Category */}
      {Object.entries(groupedToggles).map(([category, toggles]) => (
        <div key={category} className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            {getCategoryLabel(category)}
          </h3>

          <div className="space-y-3">
            {toggles.map((toggle) => {
              const isGranted = consents.get(toggle.type) || false;

              return (
                <div
                  key={toggle.type}
                  className={`p-4 border rounded-lg transition-all ${
                    isGranted
                      ? "bg-green-50 border-green-300"
                      : "bg-white border-gray-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900">
                          {toggle.labelKey}
                        </h3>
                        {toggle.required && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                            Required
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {toggle.descriptionKey}
                      </p>
                    </div>

                    <button
                      type="button"
                      role="switch"
                      aria-checked={isGranted}
                      aria-label={toggle.labelKey}
                      data-consent-type={toggle.type}
                      onClick={() => handleToggle(toggle.type, toggle)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        isGranted ? "bg-green-600" : "bg-gray-300"
                      } ${toggle.required && isGranted ? "opacity-50 cursor-not-allowed" : ""}`}
                      // A required consent must stay grantable; it is
                      // withdrawal that is forbidden. The condition was
                      // inverted, so required consents were disabled exactly
                      // while ungranted - no user could ever grant one - and
                      // became clickable once granted, which is the case
                      // handleToggle already rejects.
                      disabled={toggle.required && isGranted}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isGranted ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Consent History Button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        >
          <History className="w-5 h-5" />
          {showHistory ? "Hide" : "Show"} Consent History
        </button>
      </div>

      {/* Consent History */}
      {showHistory && (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Consent History
          </h3>

          {history.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              No consent history available
            </p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {history.map((consent) => (
                <div
                  key={consent._id}
                  data-consent-history-entry={consent.consentType}
                  className="p-3 bg-white rounded border border-gray-200 flex items-start justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900 text-sm">
                        {consentToggles.find(
                          (t) => t.type === consent.consentType,
                        )?.labelKey || consent.consentType}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${
                          consent.status === ConsentStatus.GRANTED
                            ? "bg-green-100 text-green-700"
                            : consent.status === ConsentStatus.WITHDRAWN
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {consent.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {consent.grantedAt &&
                        `Granted: ${fmt.date(consent.grantedAt)}`}
                      {consent.withdrawnAt &&
                        ` • Withdrawn: ${fmt.date(consent.withdrawnAt)}`}
                    </p>
                  </div>

                  {consent.status === ConsentStatus.GRANTED ? (
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <X className="w-5 h-5 text-red-600 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer Info */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-600">
        <p className="mb-2">
          <strong>Your Rights:</strong> You have the right to access, rectify,
          or delete your data at any time.
        </p>
        <p>
          For more information about how we process your data, please see our{" "}
          <a href="/privacy-policy" className="text-blue-600 hover:underline">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
};
